/**
 * VH-83: Library list-card. Shows photo, title, category, kcal/protein, plus
 * an equipment compatibility indicator (green check / red triangle).
 */

import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'

export default function RecipeCard({ recipe, categoryLabel }) {
  const { t } = useTranslation()
  const compat = recipe.equipment_compat
  const isCompatible = compat?.compatible ?? true

  return (
    <Link
      to={`/recipes/${recipe.id}`}
      className="block group rounded-lg overflow-hidden border border-gray-200 bg-surface-container hover:shadow-md transition"
      data-testid={`recipe-card-${recipe.id}`}
    >
      <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
        {/* VH-101: prefer recipe-level cover image; fall back to the legacy
            display photo when a recipe hasn't been migrated. */}
        {recipe.cover_image_url || recipe.photo_url ? (
          <img
            src={recipe.cover_image_url || recipe.photo_url}
            alt={recipe.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <PhotoIcon className="h-12 w-12 text-gray-300" />
        )}
      </div>
      <div className="p-3 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
            {recipe.title}
          </h3>
          {isCompatible ? (
            <CheckCircleIcon
              className="h-5 w-5 text-green-500 flex-shrink-0"
              title={t('recipes.compatibleBadge')}
              data-testid="compatible-icon"
            />
          ) : (
            <ExclamationTriangleIcon
              className="h-5 w-5 text-red-500 flex-shrink-0"
              title={t('recipes.incompatibleBadge')}
              data-testid="incompatible-icon"
            />
          )}
        </div>
        {categoryLabel && (
          <p className="text-xs text-gray-500">{categoryLabel}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>
            {t('recipes.kcalPerServing', {
              kcal: Math.round(recipe.total_kcal_per_serving || 0),
            })}
          </span>
          <span aria-hidden>·</span>
          <span>
            {t('recipes.proteinPerServing', {
              protein: Math.round(recipe.total_protein_g_per_serving || 0),
            })}
          </span>
        </div>
      </div>
    </Link>
  )
}
